/**
 * Gift codes: the account page's redeem field, the hook it redeems through, and the message for
 * each way a redemption can fail.
 */
import { LootApiError, type RedeemGiftCodeResponse } from "@loot-os/client";
import { useMutation } from "@tanstack/react-query";

import { qk } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useShopper } from "./shopper.js";

const REFUSALS: Record<string, string> = {
  unknown: "That code isn't valid. Check it and try again.",
  redeemed: "That code has already been redeemed.",
  expired: "That code has expired.",
  voided: "That code is no longer valid.",
};

/** What to tell the shopper when a code did not redeem. */
export function giftCodeErrorMessage(err: unknown): string {
  if (err instanceof LootApiError) {
    switch (err.code) {
      case "gift_code_not_redeemable": {
        const reason = err.details?.reason;
        return (typeof reason === "string" && REFUSALS[reason]) || "That code can't be redeemed.";
      }
      case "account_locked":
        return "This account is locked. Contact support.";
      case "rate_limited":
        return "Too many attempts. Wait a minute and try again.";
      default:
        return "Could not redeem the code. Try again.";
    }
  }
  return "Could not redeem the code. Try again.";
}

/** Redeems a code into the balance; the balance query refetches with the credit in it. */
export function useRedeemGiftCode() {
  const { client } = useShopper();
  return useMutation({
    mutationFn: (code: string): Promise<RedeemGiftCodeResponse> => client.redeemGiftCode(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.balance });
    },
  });
}
